import type {Object3D} from 'three';
import {
    buildResultBodyDisplayNameMap,
    buildPartFeatureDocument,
    isSketchArcObject,
    isSketchCircleObject,
    isSketchCollectionObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchPointObject,
    SketchDisplayState,
    type WorkerGeometryStateSummary,
    type SketchEntityReferenceWarningSummary,
} from '@puppycad/cad-core';
import type {FeatureTreeObjectSummary, ManagedSketchSummary} from './feature-tree-types';

/**
 * 文件定位：
 * 1. 当前文件位于 `web-client/hooks/feature-tree` 目录；
 * 2. 负责特征树模块中与“对象名称、草图摘要、对象分类”相关的纯辅助逻辑；
 * 3. 所有函数都不依赖 Vue 状态，方便在 store 与动作工厂之间复用。
 *
 * 文件职责：
 * 1. 统一管理设计树对象显示名解析；
 * 2. 提供场景草图摘要收集与草图实体类型识别；
 * 3. 提供从 Worker 状态恢复设计树对象摘要的纯函数入口。
 */

/**
 * 获取对象显示名称。
 * @param object 目标对象。
 * @returns 优先级为 `name -> metadata.name -> type` 的显示名称。
 */
export function getObjectDisplayName(
    object: { name?: string; metadata?: Record<string, any>; type?: string } | null | undefined,
): string {
    if (!object) {
        return '';
    }
    return object.name || object.metadata?.name || object.type || '';
}

/**
 * 从 Viewer 草图摘要中收集当前仍受管的草图列表。
 * @param sketchSummaries Viewer 返回的草图摘要。
 * @param deletedObjectIds 已删除对象集合。
 * @param suppressedSketchIds 已压制草图集合。
 * @returns 过滤后的草图摘要列表。
 */
export function collectManagedSketches(
    sketchSummaries: Array<{
        id: string;
        persistentId?: string;
        name: string;
        order: number;
        createdAt?: number;
        displayState?: SketchDisplayState;
        referenceWarning?: SketchEntityReferenceWarningSummary;
    }>,
    deletedObjectIds: Set<string>,
    suppressedSketchIds: Set<string>,
): ManagedSketchSummary[] {
    return sketchSummaries
        .map((summary) => ({
            id: summary.id,
            // 设计树投影需要持久 ID 才能把特征绑定的来源草图归一回场景句柄。
            persistentId: summary.persistentId,
            entityType: 'sketch',
            name: summary.name,
            order: summary.order,
            createdAt: summary.createdAt,
            /**
             * Viewer 的显示状态是草图编辑生命周期的权威来源。
             * 投影层只保留“是否正在编辑”，避免向树模型泄漏更多 Viewer 状态。
             */
            editing: summary.displayState === SketchDisplayState.Edit,
            referenceWarning: summary.referenceWarning,
        }))
        .filter((summary) => !deletedObjectIds.has(summary.id) && !suppressedSketchIds.has(summary.id));
}

/**
 * 根据当前草图名称同步下一个草图序号。
 * @param currentSerial 当前序号。
 * @param sketches 草图摘要列表。
 * @returns 同步后的下一个可用序号。
 */
export function syncSketchSerialByManagedSketches(
    currentSerial: number,
    sketches: ManagedSketchSummary[],
): number {
    let maxIndex = 0;
    sketches.forEach((sketch) => {
        /**
         * 设计树中的草图命名策略仍以“草图N”为主。
         * 这里通过名称解析序号，用来保持新建草图序号不与现有草图冲突。
         */
        const match = /草图(\d+)/.exec(sketch.name);
        if (!match) {
            return;
        }
        const index = Number(match[1]);
        if (!Number.isNaN(index)) {
            maxIndex = Math.max(maxIndex, index);
        }
    });
    return Math.max(currentSerial, maxIndex + 1);
}

/**
 * 通过场景对象类型解析草图实体种类。
 * @param object 场景对象。
 * @returns 草图实体类型；无法识别时返回 `null`。
 */
export function resolveSketchEntityTypeByMeshClass(object: Object3D): string | null {
    if (isSketchLineObject(object)) {
        return 'line';
    }
    if (isSketchPointObject(object)) {
        return 'point';
    }
    if (isSketchCircleObject(object)) {
        return 'circle';
    }
    if (isSketchArcObject(object)) {
        return 'arc';
    }
    if (isSketchEllipseObject(object)) {
        return 'ellipse';
    }
    if (isSketchCollectionObject(object)) {
        /**
         * 组合草图对象目前仍只作为矩形/多段线等容器存在。
         * 设计树侧先不把它细分到更具体实体类型，避免在宿主层发散领域语义。
         */
        return null;
    }
    return null;
}

/**
 * 从 Worker 状态中恢复设计树对象摘要。
 * @param options 恢复输入。
 * @returns 对象摘要；未命中时返回 `null`。
 */
export function resolveFeatureTreeObjectSummary(options: {
    /** Worker 状态摘要。 */
    state: WorkerGeometryStateSummary;
    /** 目标对象 ID。 */
    objectId: string;
    /** 场景草图摘要。 */
    sceneSketches: ManagedSketchSummary[];
    /** 隐藏对象集合。 */
    hiddenObjectIds: Set<string>;
    /** 压制草图集合。 */
    suppressedSketchIds: Set<string>;
    /** 名称覆写表。 */
    objectNameOverrides: Map<string, string>;
}): FeatureTreeObjectSummary | null {
    const {
        state,
        objectId,
        sceneSketches,
        hiddenObjectIds,
        suppressedSketchIds,
        objectNameOverrides,
    } = options;
    let cachedDocument: ReturnType<typeof buildPartFeatureDocument> | null = null;
    const getProjectionDocument = (): ReturnType<typeof buildPartFeatureDocument> => {
        if (!cachedDocument) {
            cachedDocument = buildPartFeatureDocument({
                history: state.history,
                objects: state.objects as never,
                sceneSketches,
                hiddenObjectIds,
                suppressedSketchIds,
                objectNameOverrides,
            });
        }
        return cachedDocument;
    };

    /**
     * 先从 Worker 对象列表中恢复。
     * 这是正式几何对象的首选来源；显示名如需对齐特征节点，再按 featureId 读取 cad-core 投影。
     */
    const object = state.objects.find((item) => item.id === objectId) ?? null;
    if (object) {
        const projectionDocument = getProjectionDocument();
        const bodyDisplayNameMap = buildResultBodyDisplayNameMap({
            objects: state.objects as never,
            features: projectionDocument.features,
            objectNameOverrides,
        });
        const createdByFeatureId = typeof object.metadata?.featureId === 'string'
            ? object.metadata.featureId
            : null;
        const featureDisplayName = createdByFeatureId
            ? projectionDocument.features.find((item) => item.id === createdByFeatureId)?.displayName
            : undefined;
        /**
         * 实体对象的默认显示名必须与创建它的特征名一致。worker metadata.name 只作为兜底，
         * 不能再让“圆角1”在实体文件夹中退化成“圆角”。
         */
        const displayName = String(
            bodyDisplayNameMap.get(object.id)
            ?? featureDisplayName
            ?? object.metadata?.name
            ?? object.type
            ?? '',
        );
        return {
            id: object.id,
            type: object.type as FeatureTreeObjectSummary['type'],
            metadata: object.metadata,
            commandId: object.commandId,
            name: displayName,
        };
    }

    /**
     * 若目标不是几何对象，则尝试通过 cad-core 的投影包装恢复特征摘要。
     * 这样 web-client 不需要自行拼装 feature kind、displayName 等语义。
     */
    const command = state.history.find((item) => item.id === objectId) ?? null;
    if (!command) {
        return null;
    }

    const document = getProjectionDocument();
    const feature = document.features.find((item) => item.id === command.id);
    return {
        id: command.id,
        type: 'feature',
        metadata: {
            featureId: command.id,
            featureKind: feature?.kind,
            sourceSketchId: feature?.sourceSketchId,
            name: feature?.displayName ?? objectNameOverrides.get(command.id) ?? command.id,
        },
        commandId: command.id,
        name: feature?.displayName ?? objectNameOverrides.get(command.id) ?? command.id,
    };
}
